import { useCallback, useState } from 'react';
import {
  ChartAIItem,
  ChatMessage,
  MapAIItem,
  useChatStore,
} from './useChatStore';

const CHAT_URL: Record<'PF' | 'PJ', string> = {
  PF: 'https://general-n8n.wcod4v.easypanel.host/webhook/e7bcbc3c-323a-49b6-8dd9-34f537201ddc/chat',
  PJ: 'https://general-n8n.wcod4v.easypanel.host/webhook/7432dd20-8460-481b-968c-53f1313d25fd/chat',
};

export const useChatClient = (
  sessionId: string,
  indexName: string,
  personType: 'PF' | 'PJ',
) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedCharts, setSelectedCharts] = useState<ChartAIItem[]>([]);
  const [selectedMap, setSelectedMap] = useState<MapAIItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getMessages, addMessage } = useChatStore();

  const messages = getMessages(sessionId);

  const sendMessage = useCallback(
    async (forcedInput?: string) => {
      if (!inputValue.trim() && !forcedInput) return;

      const userMessage: ChatMessage = {
        role: 'user',
        content: forcedInput ?? inputValue.trim(),
      };

      addMessage(sessionId, userMessage);

      if (!forcedInput) {
        setInputValue('');
      }

      setIsLoading(true);

      try {
        const response = await fetch(CHAT_URL[personType], {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatInput: forcedInput ?? inputValue,
            sessionId,
            indexName,
            personType,
          }),
        });

        if (!response.ok) throw new Error(response.statusText);

        const responseData = await response.json();

        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content:
            responseData.answer ??
            'Ocorreu um erro ao processar a mensagem. Por favor, tente novamente.',
          suggestions: responseData.suggestions,
          chartAI: responseData.charts,
          mapAI: responseData.maps,
        };

        if (responseData.charts) {
          setSelectedCharts(responseData.charts);
        }

        if (responseData.maps) {
          setSelectedMap(responseData.maps);
        }

        addMessage(sessionId, assistantMessage);
      } catch (error) {
        console.error('Error sending message:', error);
        const errorMessage: ChatMessage = {
          role: 'assistant',
          content: 'Erro ao enviar mensagem ao servidor.',
        };
        addMessage(sessionId, errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [inputValue, sessionId, addMessage],
  );

  return {
    messages,
    inputValue,
    isLoading,
    selectedCharts,
    setSelectedCharts,
    selectedMap,
    setSelectedMap,
    setInputValue,
    sendMessage,
  };
};
